import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// 기본 비밀번호
export const DEFAULT_PASSWORD = '0000';

// 약국 정보 타입
export interface Pharmacy {
  id: number;
  name: string;
  address: string;
  phone_number: string;
  is_active: boolean;
  business_registration_number?: string;
}

// Auth Store 상태 타입
interface AuthState {
  pharmacy: Pharmacy | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isDefaultPassword: boolean; // 기본 비밀번호 여부

  // 계산된 상태
  isAuthenticated: () => boolean;

  // 액션
  login: (
    businessRegistrationNumber: string,
    password: string
  ) => Promise<{ success: boolean; pharmacy?: Pharmacy; error?: string }>;
  setPassword: (
    businessRegistrationNumber: string,
    password: string,
    passwordConfirm: string,
    currentPassword?: string
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshAccessToken: () => Promise<{ success: boolean; error?: string }>;

  // 헬퍼
  setPharmacy: (pharmacy: Pharmacy | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setIsDefaultPassword: (isDefault: boolean) => void;
}

/**
 * 약국 인증 상태 관리 Zustand Store
 * - 로그인/로그아웃
 * - 비밀번호 설정
 * - 약국 정보 관리
 * - 토큰 관리
 */
const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // ==================== 상태 ====================
        pharmacy: null,
        accessToken: null,
        refreshToken: null,
        isLoading: false,
        isDefaultPassword: false,

        // ==================== 계산된 상태 ====================
        isAuthenticated: () => !!get().pharmacy && !!get().accessToken,

        // ==================== 액션: 로그인 ====================
        login: async (businessRegistrationNumber: string, password: string) => {
          set({ isLoading: true });

          try {
            const { getPharmacyAuthService } = await import(
              '../../domains/pharmacyAuth'
            );
            const pharmacyAuthService = getPharmacyAuthService();

            const cleanBrn = businessRegistrationNumber.replace(/-/g, '');

            const result = await pharmacyAuthService.login(
              cleanBrn,
              password
            );

            if (
              (result?.result_code === '200' || result?.resultCode === 200) &&
              result.data
            ) {
              const pharmacy: Pharmacy = {
                id: result.data.pharmacy.id,
                name: result.data.pharmacy.name,
                address: result.data.pharmacy.address,
                phone_number: result.data.pharmacy.phone_number,
                is_active: result.data.pharmacy.is_active,
                business_registration_number: cleanBrn,
              };

              // 기본 비밀번호 여부 판단
              const isDefault = password === DEFAULT_PASSWORD;

              set({
                pharmacy,
                accessToken: result.data.access_token,
                refreshToken: result.data.refresh_token,
                isLoading: false,
                isDefaultPassword: isDefault,
              });

              // 로그인 성공 직후 즐겨찾는 환자 목록 한 번에 fetch
              // (이후 화면들은 favoritesStore만 보고 그림)
              try {
                const { default: useFavoritesStore } = await import(
                  './favoritesStore'
                );
                useFavoritesStore.getState().fetchAll(cleanBrn);
              } catch (e) {
                console.error('[authStore] favorites fetch 실패:', e);
              }

              return { success: true, pharmacy };
            }

            throw new Error(result?.result_msg || '로그인에 실패했습니다.');
          } catch (error) {
            set({ isLoading: false });
            return { success: false, error: (error as Error).message };
          }
        },

        // ==================== 액션: 비밀번호 설정 ====================
        setPassword: async (
          businessRegistrationNumber: string,
          password: string,
          passwordConfirm: string,
          currentPassword?: string
        ) => {
          set({ isLoading: true });

          try {
            const { getPharmacyAuthService } = await import(
              '../../domains/pharmacyAuth'
            );
            const pharmacyAuthService = getPharmacyAuthService();

            const result = await pharmacyAuthService.setPassword(
              businessRegistrationNumber.replace(/-/g, ''),
              password,
              passwordConfirm,
              currentPassword
            );

            if (result?.result_code === '200' || result?.resultCode === 200) {
              set({
                isLoading: false,
                isDefaultPassword: false,
              });

              return { success: true };
            }

            throw new Error(
              result?.result_msg || '비밀번호 설정에 실패했습니다.'
            );
          } catch (error) {
            set({ isLoading: false });
            return { success: false, error: (error as Error).message };
          }
        },

        // ==================== 액션: 로그아웃 ====================
        logout: async () => {
          try {
            const { getPharmacyAuthService } = await import(
              '../../domains/pharmacyAuth'
            );
            const pharmacyAuthService = getPharmacyAuthService();
            await pharmacyAuthService.logout();
          } catch (error) {
            console.error('Logout error:', error);
          } finally {
            set({
              pharmacy: null,
              accessToken: null,
              refreshToken: null,
              isDefaultPassword: false,
            });
            // 로그아웃 시 즐겨찾기 store도 비움
            try {
              const { default: useFavoritesStore } = await import(
                './favoritesStore'
              );
              useFavoritesStore.getState().clear();
            } catch (e) {
              console.error('[authStore] favorites clear 실패:', e);
            }
          }
        },

        // ==================== 액션: 토큰 갱신 ====================
        refreshAccessToken: async () => {
          try {
            const { getPharmacyAuthService } = await import(
              '../../domains/pharmacyAuth'
            );
            const pharmacyAuthService = getPharmacyAuthService();

            const result = await pharmacyAuthService.refreshToken();

            if (
              (result?.result_code === '200' || result?.resultCode === 200) &&
              result.data
            ) {
              set({
                accessToken: result.data.access_token,
                refreshToken: result.data.refresh_token,
              });

              return { success: true };
            }

            throw new Error(result?.result_msg || '토큰 갱신에 실패했습니다.');
          } catch (error) {
            // 갱신 실패 시 로그아웃
            set({
              pharmacy: null,
              accessToken: null,
              refreshToken: null,
              isDefaultPassword: false,
            });
            return { success: false, error: (error as Error).message };
          }
        },

        // ==================== 헬퍼: 약국 정보 설정 ====================
        setPharmacy: (pharmacy: Pharmacy | null) => {
          set({ pharmacy });
        },

        // ==================== 헬퍼: 토큰 설정 ====================
        setTokens: (accessToken: string, refreshToken: string) => {
          set({ accessToken, refreshToken });
        },

        // ==================== 헬퍼: 기본 비밀번호 여부 설정 ====================
        setIsDefaultPassword: (isDefault: boolean) => {
          set({ isDefaultPassword: isDefault });
        },
      }),
      {
        name: 'pharmacy-auth-storage',
        partialize: state => ({
          pharmacy: state.pharmacy,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          isDefaultPassword: state.isDefaultPassword,
        }),
      }
    ),
    {
      name: 'pharmacy-auth-store',
      enabled: import.meta.env.DEV,
    }
  )
);

export default useAuthStore;
